import {
    createPageMetricsForResponse,
    PageType,
} from '@jet-app/music-classical/metrics/page';
import { ObjectGraph } from '@jet/environment/dependencies';
import type { DetailPage } from '@jet-app/music-classical/models/pages/detail';
import type {
    ClassicalAPIResponse,
    ClassicalAPIResponseActionButton,
    ClassicalAPIResponseHeader,
} from '@jet-app/music-classical/api/types';
import { transformSections } from '@jet-app/music-classical/utilities/transforms/sections';
import { fixArtwork } from '@jet-app/music-classical/models';
import type { ContainerDetailHeader } from '@jet-app/music-classical/models/header';
import { playActionForClassicalPlayAction } from '@jet-app/music-classical/action-model/PlayAction';
import { createFlowActionForItem } from '@jet-app/music-classical/utilities/create-flow-action';
import type { ActionModel } from '@jet/environment/types/models';
import { makeContextualMenuFetchAction } from '@jet-app/music-classical/action-model/contextual-menu/FetchAction';
import { addingGenreSeoData } from '@jet-app/music-classical/utilities/seo/Genre';
import { addingInstrumentSeoData } from '@jet-app/music-classical/utilities/seo/Instrument';
import { addingPeriodSeoData } from '@jet-app/music-classical/utilities/seo/Period';
import { addingRecordingSeoData } from '@jet-app/music-classical/utilities/seo/Recording';
import { addingWorkSeoData } from '@jet-app/music-classical/utilities/seo/Work';
import { getSubtitleLink } from '@jet-app/music-classical/utilities/transforms/shelf';
import type { ContextualMenuResponse } from '@jet-app/music-classical/utilities/should-render-contextual-menu';
import { addingMultiRoomSeoData } from '@jet-app/music-classical/utilities/seo/MultiRoom';

export function renderDetailPage(
    canonicalURL: string,
    apiResponse: ClassicalAPIResponse,
    contextualMenuResponse: ContextualMenuResponse,
    objectGraph: ObjectGraph,
    intentId?: string,
): DetailPage {
    const responseHeader = apiResponse?.body
        ?.header as unknown as ClassicalAPIResponseHeader;

    const { screenType, screenId } = apiResponse?.body;

    const page = {
        pageKind: 'componentScreen',
        kind: 'detailPage',
        screenId: screenId || intentId,
        screenType,
        header: renderDetailPageHeader(
            responseHeader,
            apiResponse?.body.screenId || intentId,
            objectGraph,
            contextualMenuResponse,
            apiResponse?.body.primaryButton,
            apiResponse?.body.secondaryButtons,
            apiResponse?.body?.userComponentsAction,
        ),
        sections: transformSections(
            apiResponse?.body?.sections as any,
            objectGraph,
            apiResponse?.body?.screenType,
            apiResponse?.body?.screenId,
        ),
        canonicalURL,
        pageMetrics: createPageMetricsForResponse(
            apiResponse,
            PageType.detailPage,
        ),
    } as DetailPage;

    if (screenType === 'multiRoom') {
        return addingMultiRoomSeoData(
            canonicalURL,
            page,
            objectGraph,
            intentId,
        );
    }

    if (screenType === 'instrument') {
        addBackgroundTreatmentToInfo(page.sections);
    }

    switch (screenType) {
        case 'genre':
            return addingGenreSeoData(canonicalURL, page, objectGraph);
        case 'instrument':
            return addingInstrumentSeoData(canonicalURL, page, objectGraph);
        case 'period':
            return addingPeriodSeoData(canonicalURL, page, objectGraph);
        case 'recording':
            return addingRecordingSeoData(canonicalURL, page, objectGraph);
        case 'work':
            return addingWorkSeoData(canonicalURL, page, objectGraph);
    }

    return page;
}

function renderDetailPageHeader(
    responseHeader: ClassicalAPIResponseHeader,
    id: string,
    objectGraph: ObjectGraph,
    contextualMenuResponse: ContextualMenuResponse,
    actionButton?: ClassicalAPIResponseActionButton,
    secondaryButtons?: ClassicalAPIResponseActionButton[],
    userComponentsAction?: any,
): ContainerDetailHeader {
    const { subtitleActions, ...rest } = responseHeader;
    const { entities = null, renderContextualMenu } = contextualMenuResponse;
    const pageHeader = { ...rest } as ContainerDetailHeader;
    pageHeader.entities = entities;

    switch (responseHeader.type) {
        // Uses ArtistDetailHeader
        case 'genre':
        case 'instrument':
        case 'period':
            pageHeader.isRoomHeader = true;
            break;
        // Uses ContainerDetailHeader
        case 'recording':
        case 'recording-with-actions':
            pageHeader.title = responseHeader.workTitle;
            pageHeader.subtitle = responseHeader.artists;
            pageHeader.metadataTop = responseHeader.composer;
            pageHeader.id = id;
            break;
        case 'work':
            pageHeader.composerAction = createFlowActionForItem(
                {
                    action: responseHeader.composerAction as ActionModel,
                },
                objectGraph,
            );
            pageHeader.id = id;
            break;
        default:
            break;
    }

    if (responseHeader.image) {
        pageHeader.artwork = fixArtwork(responseHeader.image);
    }

    if (actionButton) {
        pageHeader.primaryButton = {
            action: playActionForClassicalPlayAction(actionButton.action),
            title: actionButton.title,
        };
    }

    if (secondaryButtons && !renderContextualMenu) {
        pageHeader.shareButton = secondaryButtons.find(
            (button) => button.subtype === 'share',
        );
    }

    if (subtitleActions?.length) {
        pageHeader.subtitleLinks = subtitleActions
            .map((action) => getSubtitleLink(action, objectGraph))
            .filter(Boolean);
    }

    if (userComponentsAction && renderContextualMenu) {
        pageHeader.contextMenuAction = userComponentsAction
            ? makeContextualMenuFetchAction(userComponentsAction.url)
            : null;
    }

    return pageHeader;
}

function addBackgroundTreatmentToInfo(sections: Array<Section<unknown>>) {
    const infoSection = sections.find(
        (section) => section.sectionKind === 'info',
    );

    if (infoSection) {
        infoSection.backgroundTreatment = 'alternate';
    }
}
